import { useT } from "../../lib/use-t";
import type { VocabEntry } from "../../data/vocab/types";
import { WordAudioButton } from "./WordAudioButton";
import { lemmaWithoutArticle } from "./word-helpers";

const POS_LABEL: Record<VocabEntry["pos"], string> = {
  noun: "zelfstandig naamwoord",
  verb: "werkwoord",
  adjective: "bijvoeglijk naamwoord",
  phrase: "uitdrukking",
  other: "overig",
  unknown: "",
};

/** Right pane of the master-detail browser: the full word card. Big play,
 *  lemma + article chip, a meta line, the first example as the hero serif
 *  line, and a "SuperZinnen" block built from `entry.examples` (the real
 *  data — the label is new, the source is the same audio-enabled sentences).
 *
 *  No "Oefen deze lijst" CTA or "Markeer als geleerd" button: neither has a
 *  backend (no list-scoped practice flow, no learned-words store), so per the
 *  no-backend rule they are omitted rather than faked. */
export function WordDetailPane({ entry }: { entry: VocabEntry }) {
  const t = useT();
  const lemma = lemmaWithoutArticle(entry.dutch);

  const metaParts = [
    entry.english,
    POS_LABEL[entry.pos],
    entry.plural ? `meervoud ${entry.plural}` : null,
  ].filter(Boolean) as string[];

  // The first example anchors the card as the hero serif line; the rest fill
  // the SuperZinnen block. If there's only one, it still reads as the hero.
  const [hero, ...rest] = entry.examples;
  const superSentences = rest.length > 0 ? rest : entry.examples;

  return (
    <article className="space-y-5">
      <div className="flex items-center gap-3.5">
        <WordAudioButton text={entry.dutch} variant="icon" iconSize="lg" />
        <h2 className="text-3xl font-semibold tracking-tight">{lemma}</h2>
        {entry.gender ? (
          <span className="self-center rounded-[var(--radius-sm)] bg-[var(--accent-soft)] px-2.5 py-1 font-mono-badge text-xs text-[var(--accent)]">
            {entry.gender}
          </span>
        ) : null}
      </div>

      {metaParts.length > 0 ? (
        <p className="text-sm text-[var(--ink-muted)]">
          {metaParts.map((part, i) => (
            <span key={part}>
              {i === 0 ? <span className="text-[var(--ink)] font-medium">{part}</span> : part}
              {i < metaParts.length - 1 ? <span aria-hidden> · </span> : null}
            </span>
          ))}
        </p>
      ) : null}

      {hero ? (
        <p className="font-serif-italic text-xl text-[var(--ink)] leading-relaxed">{hero}</p>
      ) : null}

      {entry.prepositions && entry.prepositions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {entry.prepositions.map((p) => (
            <span
              key={`${entry.id}-prep-${p}`}
              className="inline-flex items-center rounded-md border border-[var(--border)] bg-[var(--panel)] px-2 py-1 font-mono-badge text-xs text-[var(--ink-muted)]"
            >
              {p}
            </span>
          ))}
        </div>
      ) : null}

      {superSentences.length > 0 ? (
        <section className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] p-4 sm:p-5">
          <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-3">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--accent)]">
              {t("woorden.detail.superHeading")}
            </span>
            <span className="text-xs text-[var(--ink-faint)]">
              {t("woorden.detail.superSub")}
            </span>
          </header>
          <ul>
            {superSentences.map((s, i) => (
              <li
                key={`${entry.id}-super-${i}`}
                className="flex items-center gap-3 py-2.5 border-t border-[var(--border)] first:border-t-0"
              >
                <WordAudioButton text={s} variant="icon" iconSize="sm" />
                <span className="text-sm text-[var(--ink)] leading-snug">{s}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {entry.usageTip ? (
        <p className="font-serif-italic text-sm text-[var(--ink-muted)] leading-snug">
          {entry.usageTip}
        </p>
      ) : null}
    </article>
  );
}
